import { useCallback, useMemo } from "react";

import { Container } from "@material-ui/core";

import { ContentSection } from "../application/ContentSection";
import { CustomerCard } from "./CustomerCard";
import { Section } from "../components/section/Section";
import { GreenButton, VerticalButtons, WhiteButton } from "../components/new-button";
import { CreateRegistrationDialogProps, useCreateRegistrationDialog } from "../registration/create-registration-dialog/CreateRegistrationDialog";
import { CreateRegistrationDialogViewModel } from "../registration/create-registration-dialog/CreateRegistrationDialog";
import { GardenerDocument } from "../gardener/gardener.model";
import { TaskResponseData } from "../task/task-response-action.hook";
import { CreateAppointmentForm, CreateAppointmentFormIds, useCreateAppointmentDialog } from "../components/form-item/client/application/CreateAppointmentDialog";
import { FormItem } from "../components/form-item/framework/core/form-item.model";
import { FormController } from "../components/form-item/framework/core/form.controller";
import { FormProps } from "../components/form-item/framework/react/FormProvider";
import { DatePickerFormItem, TextAreaFormItem } from "../components/form-item/client/components";
import { FloatingButton } from "../components/floating-button/FloatingButton";
import { GardeningTaskGardenerViewDocument } from "../gardening-task/gardener-view/gardening-task-gardener-view.model";
import { useStopWorkingForCustomerDialog } from "../components/form-item/client/application/StopWorkingForCustomerDialog";

interface CustomerDetailPageProps {
    gardener: GardenerDocument;
    gardeningTask: GardeningTaskGardenerViewDocument;
    createRegistration: CreateRegistrationDialogProps["onConfirm"];
    taskResponseAction: (data: TaskResponseData | string) => void;
    now: string;
}

export const CustomerDetailPage = (props: CustomerDetailPageProps) => {
    const { gardener, gardeningTask, createRegistration, taskResponseAction, now } = props;

    const hasFinishedWorking = useMemo(() => {
        return gardeningTask.status === "found gardener" && gardeningTask.working.status === "has finished working";
    }, [gardeningTask]);

    const initialCreateRegistrationDialogViewModel = useMemo<CreateRegistrationDialogViewModel>(() => ({
        customer: {
            id: gardeningTask.customer.id,
            name: gardeningTask.customer.name ?? "Kundens navn er ikke længere tilgængeligt",
        },
        appointmentDate: "",
        appointmentDateEnabled: true,
        appointmentDateDirty: false,
        tasks: [{
            description: "",
            descriptionDirty: false,
            duration: undefined,
            durationDirty: false,
            tools: undefined,
            toolsDirty: false,
        }],
        now: now,
    }), [gardeningTask, now])

    const openCreateRegistrationDialog = useCreateRegistrationDialog(initialCreateRegistrationDialogViewModel, createRegistration);

    const createAppointmentActionAdapter = useCallback<FormProps<CreateAppointmentForm>["onSubmit"]>((controller, form): void => {
        const data = mapCreateAppointmentFormToTaskResponseData(controller, form, gardeningTask.customer.id);

        taskResponseAction(data);
    }, [taskResponseAction, gardeningTask.customer.id]);

    const openCreateAppointmentDialog = useCreateAppointmentDialog(now, createAppointmentActionAdapter);

    const stopWorkingForCustomerActionAdapter = useCallback<FormProps<FormItem>["onSubmit"]>((): void => {
        taskResponseAction({
            action: "stop working for customer",
            customerId: gardeningTask.customer.id,
        });
    }, [taskResponseAction, gardeningTask.customer.id]);

    const openStopWorkingForCustomerDialog = useStopWorkingForCustomerDialog(gardeningTask.customer.name, stopWorkingForCustomerActionAdapter);

    return (
        <ContentSection>
            <Container maxWidth="sm" disableGutters>

                <Section>
                    <CustomerCard title={gardeningTask.customer.name ?? "Kundens oplysninger er ikke tilgængelige"} gardeningTask={gardeningTask} />

                    {gardener.allowRegistrationBackdoor ? (
                        <FloatingButton>
                            <VerticalButtons>
                                <GreenButton onClick={openCreateRegistrationDialog}>Registrer tid</GreenButton>
                            </VerticalButtons>
                        </FloatingButton>
                    ) : hasFinishedWorking ? (
                        <FloatingButton>
                            <VerticalButtons>
                                <GreenButton onClick={openCreateAppointmentDialog}>Kunden har brug for hjælp igen</GreenButton>
                                <WhiteButton onClick={(openStopWorkingForCustomerDialog)}>Fjern kunden fra min liste</WhiteButton>
                            </VerticalButtons>
                        </FloatingButton>
                    ) : null}

                </Section>

            </Container>
        </ContentSection>
    );
};

const mapCreateAppointmentFormToTaskResponseData = (controller: FormController, form: FormItem, customerId: string): TaskResponseData => {
    const date = (controller.getItem(CreateAppointmentFormIds.DatePickerDate, form) as DatePickerFormItem).value;
    const description = (controller.getItem(CreateAppointmentFormIds.TextAreaDescription, form) as TextAreaFormItem).value;

    return {
        action: "create additional appointment",
        customerId,
        date,
        description,
    };
};
